// AIJobMatchFeed.jsx — Professional Jobs Board
// Left sidebar: filters + profile card + stats
// Main: search bar + job cards with match scores
// Right panel (desktop): selected job detail

import { useState, useEffect, useRef, useCallback } from "react";
import { computeKeywordScore } from "./AIMatchEngine";

const C = {
  green:"#0a6e4e", greenD:"#084f38", greenL:"#e8f5f0", greenLL:"#f0faf6",
  gold:"#f5a623",  goldL:"#fef9ed",  goldD:"#d4891a",
  navy:"#0f1923",  inkM:"#2d3d4f",   inkL:"#6b7a8d",
  border:"#e2e8e5",surface:"#f7f9f8",white:"#ffffff",
  blue:"#1d6fcc",  blueL:"#dbeafe",
  red:"#dc2626",   redL:"#fee2e2",
  purple:"#7c3aed",purpleL:"#f5f3ff",
  amber:"#d97706", amberL:"#fef3c7",
};

const CSS = `
  @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  .skeleton{background:linear-gradient(90deg,#eef1ef 25%,#f7f9f8 50%,#eef1ef 75%);
    background-size:400px 100%;animation:shimmer 1.4s infinite;border-radius:8px}
  .job-card{transition:all .22s cubic-bezier(.22,1,.36,1)}
  .job-card:hover{transform:translateY(-2px);box-shadow:0 8px 32px rgba(10,110,78,.1)!important}
  .job-card.selected{border-color:#0a6e4e!important;box-shadow:0 0 0 3px rgba(10,110,78,.12)!important}
  .filter-chip{padding:6px 14px;border-radius:100px;border:1.5px solid #e2e8e5;
    background:#fff;font-size:12px;font-weight:700;cursor:pointer;
    font-family:inherit;transition:all .15s;white-space:nowrap;display:inline-flex;align-items:center;gap:5px}
  .filter-chip:hover{border-color:#0a6e4e;color:#0a6e4e}
  .filter-chip.active{background:#0a6e4e;color:#fff;border-color:#0a6e4e}
  .sidebar-section{background:#fff;border:1.5px solid #e2e8e5;border-radius:14px;padding:18px;margin-bottom:14px}
  @media(max-width:640px){
    .feed-layout{grid-template-columns:1fr!important;padding:12px!important}
    .feed-sidebar{position:static!important;display:none}
    .feed-sidebar.open{display:block!important}
    .feed-filter-bar{display:flex!important}
    .job-card{margin-bottom:10px}
  }
  @media(min-width:641px){
    .feed-filter-bar{display:none!important}
  }
  .apply-btn{width:100%;padding:13px;background:linear-gradient(135deg,#0a6e4e,#084f38);
    color:#fff;border:none;border-radius:11px;font-weight:800;font-size:14px;
    cursor:pointer;font-family:inherit;transition:all .18s;
    box-shadow:0 4px 16px rgba(10,110,78,.25)}
  .apply-btn:hover{transform:translateY(-1px);box-shadow:0 8px 24px rgba(10,110,78,.3)}
  .tag-chip{font-size:11px;font-weight:600;padding:3px 10px;border-radius:100px;
    display:inline-flex;align-items:center;gap:3px}
  .scroll-jobs{overflow-y:auto;scrollbar-width:thin;scrollbar-color:#d1d9d5 transparent}
  .scroll-jobs::-webkit-scrollbar{width:5px}
  .scroll-jobs::-webkit-scrollbar-thumb{background:#d1d9d5;border-radius:10px}
`;

// ── Match score ring ──────────────────────────────────────────────────────────
function MatchRing({ score, size = 52 }) {
  const r    = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  const color = score >= 80 ? C.green : score >= 60 ? C.gold : score >= 40 ? C.blue : C.inkL;
  return (
    <svg width={size} height={size} style={{ transform:"rotate(-90deg)", flexShrink:0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={`${color}22`} strokeWidth={5}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={color} strokeWidth={5}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
        style={{ transition:"stroke-dasharray 1s cubic-bezier(.22,1,.36,1)" }}/>
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        style={{ transform:`rotate(90deg)`, transformOrigin:`${size/2}px ${size/2}px` }}
        fill={color} fontSize={size < 50 ? 10 : 12} fontWeight="900"
        fontFamily="'Fraunces',serif">
        {score}%
      </text>
    </svg>
  );
}

function MatchBadge({ score }) {
  if (score >= 85) return <span style={{ fontSize:10, fontWeight:800, color:C.green,
    background:C.greenL, padding:"2px 8px", borderRadius:100 }}>🔥 Top Match</span>;
  if (score >= 70) return <span style={{ fontSize:10, fontWeight:800, color:C.blue,
    background:C.blueL, padding:"2px 8px", borderRadius:100 }}>⭐ Strong</span>;
  if (score >= 50) return <span style={{ fontSize:10, fontWeight:800, color:C.gold,
    background:C.goldL, padding:"2px 8px", borderRadius:100 }}>👍 Good</span>;
  return null;
}

// ── Job card (compact for list) ───────────────────────────────────────────────
function JobCard({ job, profile, selected, onSelect, onSave, saved }) {
  const score = job._score || 0;
  const isNew = job._isNew;
  return (
    <div className={`job-card ${selected ? "selected" : ""}`}
      onClick={() => onSelect(job)}
      style={{ background:C.white, border:`1.5px solid ${selected ? C.green : C.border}`,
        borderRadius:14, padding:"16px", marginBottom:10,
        cursor:"pointer", position:"relative",
        boxShadow: selected ? `0 0 0 3px rgba(10,110,78,.12)` :
          score >= 80 ? "0 2px 12px rgba(10,110,78,.08)" : "none",
      }}>
      {/* Top accent line */}
      {score >= 80 && (
        <div style={{ position:"absolute", top:0, left:0, right:0, height:2,
          background:`linear-gradient(90deg,${C.green},#16a34a)`,
          borderRadius:"14px 14px 0 0" }}/>
      )}

      <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
        {/* Logo */}
        <div style={{ width:42, height:42, borderRadius:10, flexShrink:0,
          background:score >= 80 ? C.greenLL : C.surface,
          border:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:18, overflow:"hidden" }}>
          {job.logo_url
            ? <img src={job.logo_url} alt="" style={{ width:34, height:34, objectFit:"contain", borderRadius:6 }}/>
            : "🏢"}
        </div>

        {/* Info */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6,
            flexWrap:"wrap", marginBottom:2 }}>
            <span style={{ fontWeight:800, fontSize:14, color:C.navy,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {job.title}
            </span>
            <MatchBadge score={score}/>
            {isNew && <span style={{ fontSize:9, fontWeight:800, color:C.blue,
              background:C.blueL, padding:"1px 6px", borderRadius:100 }}>NEW</span>}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
            <span style={{ fontSize:12, fontWeight:600, color:C.inkM }}>{job.company_name}</span>
            {job.isExternal && (
              <span style={{ fontSize:10, fontWeight:700, padding:"1px 7px",
                borderRadius:100, background:(job.sourceColor||"#6b7a8d")+"22",
                color:job.sourceColor||"#6b7a8d" }}>
                {job.sourceLabel}
              </span>
            )}
          </div>
          <div style={{ display:"flex", gap:8, marginTop:4, flexWrap:"wrap" }}>
            {job.location && (
              <span style={{ fontSize:11, color:C.inkL }}>📍 {job.location}</span>
            )}
            {job.salary && (
              <span style={{ fontSize:11, fontWeight:700, color:C.amber }}>
                ₱{Number(job.salary).toLocaleString()}/mo
              </span>
            )}
          </div>
          {/* Tags */}
          {job.tags?.length > 0 && (
            <div style={{ display:"flex", gap:4, marginTop:6, flexWrap:"wrap" }}>
              {job.tags.slice(0,3).map(t=>(
                <span key={t} className="tag-chip"
                  style={{ background: profile?.skills?.toLowerCase().includes(t.toLowerCase())
                    ? C.greenL : C.surface,
                    color: profile?.skills?.toLowerCase().includes(t.toLowerCase())
                      ? C.green : C.inkL,
                    border:`1px solid ${profile?.skills?.toLowerCase().includes(t.toLowerCase())
                      ? C.green+"44" : C.border}` }}>
                  {profile?.skills?.toLowerCase().includes(t.toLowerCase()) ? "✓ " : ""}{t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right: match ring + save */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flexShrink:0 }}>
          <MatchRing score={score} size={46}/>
          <button onClick={e=>{ e.stopPropagation(); onSave(job.id); }}
            style={{ background:"none", border:"none", cursor:"pointer",
              fontSize:14, padding:"2px", lineHeight:1 }}>
            {saved ? "⭐" : "☆"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Job detail panel (right side) ─────────────────────────────────────────────
function JobDetail({ job, profile, onApply, onSave, saved, onClose }) {
  if (!job) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", height:"100%", color:C.inkL,
      padding:40, textAlign:"center" }}>
      <div style={{ fontSize:48, marginBottom:16 }}>👈</div>
      <div style={{ fontFamily:"'Fraunces',serif", fontSize:18,
        fontWeight:800, color:C.inkM, marginBottom:8 }}>
        Select a job to view details
      </div>
      <div style={{ fontSize:13, lineHeight:1.6 }}>
        Click any job card on the left to see the full description and apply
      </div>
    </div>
  );

  const score = job._score || 0;
  const scoreColor = score >= 80 ? C.green : score >= 60 ? C.gold : score >= 40 ? C.blue : C.inkL;
  const daysAgo = Math.floor((Date.now() - new Date(job.created_at)) / 86400000);

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column",
      overflow:"hidden" }}>
      {/* Header */}
      <div style={{ padding:"20px 24px", borderBottom:`1px solid ${C.border}`,
        background:"#fff", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
          {/* Logo */}
          <div style={{ width:56, height:56, borderRadius:14, flexShrink:0,
            background:score >= 80 ? C.greenLL : C.surface,
            border:`1.5px solid ${C.border}`,
            display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:26, overflow:"hidden" }}>
            {job.logo_url
              ? <img src={job.logo_url} alt="" style={{ width:46, height:46, objectFit:"contain", borderRadius:8 }}/>
              : "🏢"}
          </div>

          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:20,
              fontWeight:900, color:C.navy, marginBottom:3, lineHeight:1.2 }}>
              {job.title}
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:C.inkM }}>
              {job.company_name}
            </div>
            <div style={{ display:"flex", gap:12, marginTop:6, flexWrap:"wrap" }}>
              {job.location && <span style={{ fontSize:12, color:C.inkL }}>📍 {job.location}</span>}
              {job.salary && <span style={{ fontSize:12, fontWeight:700, color:C.amber }}>
                ₱{Number(job.salary).toLocaleString()}/mo
              </span>}
              <span style={{ fontSize:12, color:C.inkL }}>
                🕐 {daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo}d ago`}
              </span>
            </div>
          </div>

          {/* Match score */}
          <div style={{ textAlign:"center", flexShrink:0 }}>
            <MatchRing score={score} size={60}/>
            <div style={{ fontSize:10, color:C.inkL, fontWeight:600, marginTop:2 }}>AI Match</div>
          </div>
        </div>

        {/* Match breakdown */}
        {profile && job._breakdown && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr",
            gap:8, marginTop:14 }}>
            {[
              { label:"Skills", val:job._breakdown.skills,   color:C.green },
              { label:"Course", val:job._breakdown.course,   color:C.blue },
              { label:"Location", val:job._breakdown.location, color:C.gold, max:20 },
            ].map(b=>(
              <div key={b.label} style={{ background:C.surface,
                borderRadius:10, padding:"8px 10px" }}>
                <div style={{ display:"flex", justifyContent:"space-between",
                  fontSize:10, marginBottom:4 }}>
                  <span style={{ color:C.inkL, fontWeight:600 }}>{b.label}</span>
                  <span style={{ color:b.color, fontWeight:800 }}>
                    {b.val}/{b.max||100}
                  </span>
                </div>
                <div style={{ height:4, background:`${b.color}22`, borderRadius:2 }}>
                  <div style={{ height:"100%", borderRadius:2, background:b.color,
                    width:`${(b.val/(b.max||100))*100}%`,
                    transition:"width 1s ease" }}/>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="scroll-jobs" style={{ flex:1, padding:"20px 24px",
        overflowY:"auto" }}>

        {/* Tags */}
        {job.tags?.length > 0 && (
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.inkL,
              textTransform:"uppercase", letterSpacing:".05em",
              marginBottom:8 }}>Skills & Keywords</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {job.tags.map(t=>(
                <span key={t} className="tag-chip"
                  style={{ background: profile?.skills?.toLowerCase().includes(t.toLowerCase())
                    ? C.greenL : C.surface,
                    color: profile?.skills?.toLowerCase().includes(t.toLowerCase())
                      ? C.green : C.inkM,
                    border:`1px solid ${profile?.skills?.toLowerCase().includes(t.toLowerCase())
                      ? C.green+"44" : C.border}`,
                    fontSize:12, padding:"4px 12px" }}>
                  {profile?.skills?.toLowerCase().includes(t.toLowerCase()) ? "✓ " : ""}{t}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {job.description && (
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.inkL,
              textTransform:"uppercase", letterSpacing:".05em",
              marginBottom:8 }}>About This Role</div>
            <div style={{ fontSize:13, color:C.inkM, lineHeight:1.8,
              whiteSpace:"pre-line" }}>
              {job.description}
            </div>
          </div>
        )}

        {/* Requirements */}
        {job.requirements && (
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.inkL,
              textTransform:"uppercase", letterSpacing:".05em",
              marginBottom:8 }}>Requirements</div>
            <div style={{ fontSize:13, color:C.inkM, lineHeight:1.8,
              whiteSpace:"pre-line" }}>
              {job.requirements}
            </div>
          </div>
        )}

        {/* Details grid */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr",
          gap:10, marginBottom:20 }}>
          {[
            { icon:"🎓", label:"OJT Type",    val:job.ojt_type || "On-site" },
            { icon:"⏱",  label:"Duration",    val:job.duration || "486 hours" },
            { icon:"👥",  label:"Slots",       val:job.slots ? `${job.slots} open` : "Open" },
            { icon:"📅",  label:"Start Date",  val:job.start_date || "Flexible" },
          ].filter(d=>d.val).map(d=>(
            <div key={d.label} style={{ background:C.surface, borderRadius:10,
              padding:"10px 12px" }}>
              <div style={{ fontSize:11, color:C.inkL, marginBottom:3 }}>
                {d.icon} {d.label}
              </div>
              <div style={{ fontSize:13, fontWeight:700, color:C.navy }}>
                {d.val}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action bar */}
      <div style={{ padding:"16px 24px", borderTop:`1px solid ${C.border}`,
        background:"#fff", flexShrink:0,
        display:"flex", gap:10 }}>
        {job.isExternal
          ? <a href={job.applyUrl} target="_blank" rel="noopener noreferrer"
              className="apply-btn"
              style={{ flex:1, display:"flex", alignItems:"center",
                justifyContent:"center", gap:6, textDecoration:"none",
                background: job.sourceColor || "#0a6e4e" }}>
              Apply on {job.sourceLabel} ↗
            </a>
          : <button className="apply-btn" style={{ flex:1 }}
              onClick={() => onApply(job)}>
              Apply Now →
            </button>
        }
        <button onClick={() => onSave(job.id)}
          style={{ padding:"12px 16px",
            background:saved ? C.goldL : C.white,
            border:`1.5px solid ${saved ? C.gold : C.border}`,
            borderRadius:11, cursor:"pointer",
            fontSize:18, transition:"all .15s" }}>
          {saved ? "⭐" : "☆"}
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AIJobMatchFeed({ user, supabase, notify, onApply, onLoginPrompt }) {
  const [jobs,        setJobs]       = useState([]);
  const [profile,     setProfile]    = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [saved,       setSaved]      = useState(new Set());
  const [selected,    setSelected]   = useState(null);
  const [visibleCount, setVisibleCount] = useState(12);
  const [searchQ,     setSearchQ]    = useState("");
  const [noProfile,   setNoProfile]  = useState(false);

  // Sidebar filters
  const [filterMatch,    setFilterMatch]    = useState("all");   // all|top|strong|good
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterType,     setFilterType]     = useState("all");   // all|onsite|hybrid|remote
  const [sortBy,         setSortBy]         = useState("match");

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    let prof = null;
    if (user?.id) {
      const { data: p } = await supabase.from("profiles")
        .select("*").eq("id", user.id).single();
      prof = p; setProfile(p);
      if (!p?.skills && !p?.course) setNoProfile(true);
      const { data: sv, error: svErr } = await supabase.from("saved_jobs")
        .select("job_id").eq("student_id", user.id);
      if (!svErr) setSaved(new Set((sv||[]).map(s => s.job_id)));
    }
    // Fetch direct employer jobs
    const { data: jobData, error: jobError } = await supabase
      .from("job_listings")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending:false })
      .limit(100);

    if (jobError) console.error("Jobs load error:", jobError.message);

    // Fetch curated external jobs (JobStreet, Indeed, LinkedIn, company pages)
    const { data: extData, error: extError } = await supabase
      .from("external_jobs")
      .select("*")
      .eq("is_active", true)
      .gt("expires_at", new Date().toISOString())
      .order("is_featured", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(100);

    if (extError) console.error("External jobs error:", extError.message);

    const SOURCE_COLORS = {
      jobstreet:"#e8523a", indeed:"#003a9b",
      linkedin:"#0a66c2", company:"#6b7a8d", kalibrr:"#7c3aed",
    };
    const SOURCE_LABELS = {
      jobstreet:"JobStreet", indeed:"Indeed",
      linkedin:"LinkedIn", company:"Company", kalibrr:"Kalibrr",
    };

    const externalMapped = (extData||[]).map(j => ({
      id: "ext-" + j.id,
      title: j.title,
      company_name: j.company,
      location: j.location || "Philippines",
      description: j.description || "",
      tags: j.tags || [],
      salary: j.salary || "Allowance TBD",
      is_active: true,
      is_featured: j.is_featured,
      created_at: j.created_at,
      isExternal: true,
      applyUrl: j.source_url,
      sourceLabel: SOURCE_LABELS[j.source] || j.source,
      sourceColor: SOURCE_COLORS[j.source] || "#6b7a8d",
      courses: j.courses || [],
      duration: j.duration || "3-6 months",
      slots: j.slots || 1,
    }));

    // Direct jobs first, then featured external, then rest
    const featuredExt = externalMapped.filter(j => j.is_featured);
    const regularExt  = externalMapped.filter(j => !j.is_featured);
    const allJobs = [...(jobData||[]), ...featuredExt, ...regularExt];

    const scored = allJobs.map(job => {
      const sd = prof
        ? computeKeywordScore(job, prof)
        : { score: Math.floor(Math.random()*40)+30, skills:0, course:0, location:0 };
      const safeScore = sd || { score: 50, skills:0, course:0, location:0 };
      return {
        ...job,
        company_name: job.company_name || "",
        logo_url: job.logo_url || null,
        _score: typeof safeScore === "object" ? (safeScore.score || 50) : (safeScore || 50),
        _breakdown: typeof safeScore === "object" ? safeScore : null,
        _isNew: (Date.now() - new Date(job.created_at)) < 7*86400000,
      };
    });
    setJobs(scored);
    setLoading(false);
  };

  const handleSave = async (jobId) => {
    if (!user) { onLoginPrompt?.(); return; }
    const isSaved = saved.has(jobId);
    if (isSaved) {
      await supabase.from("saved_jobs").delete()
        .eq("student_id", user.id).eq("job_id", jobId);
      setSaved(p => { const n=new Set(p); n.delete(jobId); return n; });
    } else {
      await supabase.from("saved_jobs").insert({ student_id:user.id, job_id:jobId });
      setSaved(p => new Set([...p, jobId]));
    }
  };

  const handleApply = (job) => {
    if (!user) { onLoginPrompt?.(); return; }
    onApply?.(job);
  };

  // Derive unique locations and types for sidebar filters
  const locations = ["all", ...new Set(jobs.map(j=>j.location).filter(Boolean).map(l=>l.split(",")[0].trim()))].slice(0,8);
  const topMatchCount   = jobs.filter(j=>j._score>=80).length;
  const strongMatchCount= jobs.filter(j=>j._score>=60&&j._score<80).length;

  // Apply all filters
  // Reset visible count when filters change
  useEffect(() => { setVisibleCount(12); }, [jobs.length]);

  const filtered = jobs
    .filter(j => {
      const q = searchQ.toLowerCase();
      const matchSearch = !q ||
        j.title?.toLowerCase().includes(q) ||
        j.company_name?.toLowerCase().includes(q) ||
        j.location?.toLowerCase().includes(q) ||
        (j.tags||[]).some(t=>t.toLowerCase().includes(q));
      const matchScore =
        filterMatch==="top"    ? j._score>=80 :
        filterMatch==="strong" ? j._score>=60&&j._score<80 :
        filterMatch==="good"   ? j._score>=40&&j._score<60 : true;
      const matchLoc = filterLocation==="all" ||
        j.location?.toLowerCase().includes(filterLocation.toLowerCase());
      const matchType = filterType==="all" ||
        (j.ojt_type||"onsite").toLowerCase().includes(filterType);
      return matchSearch && matchScore && matchLoc && matchType;
    })
    .sort((a,b) => {
      if (sortBy==="match") return b._score - a._score;
      if (sortBy==="new")   return new Date(b.created_at) - new Date(a.created_at);
      return 0;
    });

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif",
      minHeight:"100vh", background:C.surface }}>
      <style>{CSS}</style>

      {/* ── Page header ─────────────────────────────────────────── */}
      <div style={{ background:"linear-gradient(135deg,#0a6e4e,#084f38)",
        padding:"32px clamp(16px,4vw,48px) 24px" }}>
        <div style={{ maxWidth:1280, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"flex-end",
            justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
            <div>
              <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,4vw,32px)",
                fontWeight:900, color:"#fff", marginBottom:6 }}>
                OJT & Internship Jobs
                {topMatchCount > 0 && (
                  <span style={{ marginLeft:14, fontSize:14, fontWeight:700,
                    color:"#f5a623", background:"rgba(245,166,35,.15)",
                    padding:"3px 12px", borderRadius:100, verticalAlign:"middle" }}>
                    🔥 {topMatchCount} top matches
                  </span>
                )}
              </h1>
              <p style={{ color:"rgba(255,255,255,.6)", fontSize:14 }}>
                {jobs.length} listings ·{" "}
                {profile
                  ? `Ranked by AI compatibility with your ${profile.course || "profile"}`
                  : "Sign in to see personalized AI match scores"}
              </p>
            </div>
            {/* Search bar in header */}
            <div style={{ position:"relative", width:"clamp(240px,35vw,380px)" }}>
              <input value={searchQ}
                onChange={e=>setSearchQ(e.target.value)}
                placeholder="Search jobs, companies, skills…"
                style={{ width:"100%", padding:"11px 16px 11px 40px",
                  borderRadius:100, border:"none", background:"rgba(255,255,255,.12)",
                  color:"#fff", fontSize:13, fontFamily:"inherit",
                  outline:"none", backdropFilter:"blur(10px)",
                  boxSizing:"border-box" }}/>
              <span style={{ position:"absolute", left:14, top:"50%",
                transform:"translateY(-50%)", fontSize:15,
                pointerEvents:"none" }}>🔍</span>
              {searchQ && (
                <button onClick={()=>setSearchQ("")}
                  style={{ position:"absolute", right:14, top:"50%",
                    transform:"translateY(-50%)", background:"none",
                    border:"none", color:"rgba(255,255,255,.6)",
                    cursor:"pointer", fontSize:14, padding:0 }}>✕</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Three-column layout ─────────────────────────────────── */}
      <div className="feed-layout" style={{ maxWidth:1280, margin:"0 auto",
        padding:"20px clamp(12px,3vw,32px)",
        display:"grid",
        gridTemplateColumns:"240px 1fr",
        gap:20, alignItems:"start" }}>

        {/* ── LEFT SIDEBAR ──────────────────────────────────────── */}
        <div className="feed-sidebar" style={{ position:"sticky", top:80 }}>

          {/* Profile card */}
          {user && profile ? (
            <div className="sidebar-section"
              style={{ background:`linear-gradient(135deg,#0a6e4e,#084f38)`,
                border:"none", marginBottom:14 }}>
              <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:12 }}>
                <div style={{ width:40, height:40, borderRadius:"50%",
                  background:"rgba(255,255,255,.15)", flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontWeight:800, fontSize:16, color:"#fff" }}>
                  {(profile.full_name||user.email||"?")[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight:800, fontSize:13, color:"#fff" }}>
                    {profile.full_name?.split(" ")[0] || "Student"}
                  </div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,.6)" }}>
                    {profile.course || "Add your course →"}
                  </div>
                </div>
              </div>
              {noProfile ? (
                <button
                  onClick={()=>window.dispatchEvent(new CustomEvent("ojthub:setPage",{detail:"profile"}))}
                  style={{ width:"100%", padding:"8px", background:"rgba(255,255,255,.15)",
                    border:"1px solid rgba(255,255,255,.25)", borderRadius:8,
                    color:"#fff", fontSize:12, fontWeight:700,
                    cursor:"pointer", fontFamily:"inherit" }}>
                  ⚡ Complete profile for AI matching →
                </button>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr",
                  gap:6, marginTop:4 }}>
                  {[
                    { val:topMatchCount, label:"Top matches" },
                    { val:saved.size,    label:"Saved" },
                  ].map(s=>(
                    <div key={s.label} style={{ background:"rgba(255,255,255,.1)",
                      borderRadius:8, padding:"8px", textAlign:"center" }}>
                      <div style={{ fontFamily:"'Fraunces',serif", fontSize:20,
                        fontWeight:900, color:"#f5a623" }}>{s.val}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,.6)" }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="sidebar-section"
              style={{ textAlign:"center", marginBottom:14 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🎯</div>
              <div style={{ fontWeight:800, fontSize:13, color:C.navy, marginBottom:4 }}>
                Get AI Match Scores
              </div>
              <div style={{ fontSize:12, color:C.inkL, marginBottom:12, lineHeight:1.5 }}>
                Sign in and complete your profile to see how well each job matches you
              </div>
              <button onClick={onLoginPrompt}
                style={{ width:"100%", padding:"9px",
                  background:`linear-gradient(135deg,${C.green},${C.greenD})`,
                  color:"#fff", border:"none", borderRadius:9,
                  fontWeight:800, fontSize:13, cursor:"pointer",
                  fontFamily:"inherit" }}>
                Sign In →
              </button>
            </div>
          )}

          {/* Match filter */}
          <div className="sidebar-section">
            <div style={{ fontSize:11, fontWeight:800, color:C.inkL,
              textTransform:"uppercase", letterSpacing:".06em",
              marginBottom:10 }}>Match Score</div>
            {[
              { id:"all",    label:"All Jobs",       count:jobs.length },
              { id:"top",    label:"🔥 Top Match",   count:topMatchCount,    color:C.green },
              { id:"strong", label:"⭐ Strong Match", count:strongMatchCount, color:C.blue },
              { id:"good",   label:"👍 Good Match",   count:jobs.filter(j=>j._score>=40&&j._score<60).length, color:C.gold },
            ].map(f=>(
              <button key={f.id}
                onClick={()=>setFilterMatch(f.id)}
                style={{ display:"flex", justifyContent:"space-between",
                  alignItems:"center", width:"100%", padding:"8px 10px",
                  borderRadius:9, border:"none", marginBottom:4,
                  background:filterMatch===f.id ? C.greenL : "transparent",
                  color:filterMatch===f.id ? C.green : C.inkM,
                  fontWeight:filterMatch===f.id ? 800 : 600,
                  fontSize:13, cursor:"pointer", fontFamily:"inherit",
                  textAlign:"left", transition:"all .15s" }}>
                <span>{f.label}</span>
                <span style={{ fontSize:11, fontWeight:700,
                  color:filterMatch===f.id ? C.green : C.inkL,
                  background:filterMatch===f.id ? C.green+"22" : C.surface,
                  padding:"1px 7px", borderRadius:100 }}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>

          {/* Location filter */}
          {locations.length > 1 && (
            <div className="sidebar-section">
              <div style={{ fontSize:11, fontWeight:800, color:C.inkL,
                textTransform:"uppercase", letterSpacing:".06em",
                marginBottom:10 }}>Location</div>
              {locations.map(loc=>(
                <button key={loc}
                  onClick={()=>setFilterLocation(loc)}
                  style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", width:"100%", padding:"7px 10px",
                    borderRadius:9, border:"none", marginBottom:3,
                    background:filterLocation===loc ? C.greenL : "transparent",
                    color:filterLocation===loc ? C.green : C.inkM,
                    fontWeight:filterLocation===loc ? 800 : 500,
                    fontSize:13, cursor:"pointer", fontFamily:"inherit",
                    textAlign:"left", transition:"all .15s" }}>
                  {loc === "all" ? "📍 All Locations" : `📍 ${loc}`}
                </button>
              ))}
            </div>
          )}

          {/* Sort */}
          <div className="sidebar-section">
            <div style={{ fontSize:11, fontWeight:800, color:C.inkL,
              textTransform:"uppercase", letterSpacing:".06em",
              marginBottom:10 }}>Sort By</div>
            {[
              { id:"match", label:"🎯 Best Match" },
              { id:"new",   label:"🆕 Newest First" },
            ].map(s=>(
              <button key={s.id} onClick={()=>setSortBy(s.id)}
                style={{ display:"block", width:"100%", padding:"8px 10px",
                  borderRadius:9, border:"none", marginBottom:4,
                  background:sortBy===s.id ? C.greenL : "transparent",
                  color:sortBy===s.id ? C.green : C.inkM,
                  fontWeight:sortBy===s.id ? 800 : 500,
                  fontSize:13, cursor:"pointer", fontFamily:"inherit",
                  textAlign:"left", transition:"all .15s" }}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Reset */}
          {(filterMatch!=="all"||filterLocation!=="all"||searchQ) && (
            <button
              onClick={()=>{ setFilterMatch("all"); setFilterLocation("all");
                setFilterType("all"); setSearchQ(""); }}
              style={{ width:"100%", padding:"8px", background:C.redL,
                border:`1px solid ${C.red}22`, borderRadius:9,
                color:C.red, fontWeight:700, fontSize:12,
                cursor:"pointer", fontFamily:"inherit" }}>
              ✕ Clear Filters
            </button>
          )}
        </div>

        {/* ── MOBILE FILTER BAR ─────────────────────────────────── */}
        <div className="feed-filter-bar" style={{ display:"none", gap:8,
          padding:"10px 0", borderBottom:"1px solid #e2e8e5",
          marginBottom:12, flexWrap:"wrap", alignItems:"center",
          gridColumn:"1/-1" }}>
          <select style={{ flex:1, padding:"8px 12px", borderRadius:8,
            border:"1.5px solid #e2e8e5", fontSize:13, background:"#fff",
            fontFamily:"inherit", color:"#0f1923" }}
            value={filterMatch} onChange={e=>setFilterMatch(e.target.value)}>
            <option value="all">All Jobs</option>
            <option value="top">🔥 Top Match</option>
            <option value="strong">⭐ Strong Match</option>
            <option value="good">👍 Good Match</option>
          </select>
          <select style={{ flex:1, padding:"8px 12px", borderRadius:8,
            border:"1.5px solid #e2e8e5", fontSize:13, background:"#fff",
            fontFamily:"inherit", color:"#0f1923" }}
            value={filterLocation} onChange={e=>setFilterLocation(e.target.value)}>
            {locations.map(l=><option key={l} value={l}>{l==="all"?"📍 All Locations":l}</option>)}
          </select>
          <input placeholder="🔍 Search..." value={searchQ}
            onChange={e=>setSearchQ(e.target.value)}
            style={{ flex:2, minWidth:140, padding:"8px 12px", borderRadius:8,
              border:"1.5px solid #e2e8e5", fontSize:13,
              fontFamily:"inherit", outline:"none" }} />
        </div>

        {/* ── RIGHT SECTION: list + detail ──────────────────────── */}
        <div style={{ display:"grid",
          gridTemplateColumns: selected ? "1fr 420px" : "1fr",
          gap:16, alignItems:"start", minWidth:0 }}>

          {/* Jobs list */}
          <div>
            {/* Results bar */}
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
              <div style={{ fontSize:13, fontWeight:600, color:C.inkL }}>
                <strong style={{ color:C.navy }}>{filtered.length}</strong> jobs
                {searchQ && ` matching "${searchQ}"`}
                {filterMatch!=="all" && ` · ${filterMatch} match`}
                {filterLocation!=="all" && ` · ${filterLocation}`}
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {["all","top","strong"].map(f=>(
                  <button key={f} className={`filter-chip ${filterMatch===f?"active":""}`}
                    onClick={()=>setFilterMatch(f)}>
                    {f==="all"?"All":f==="top"?"🔥 Top":"⭐ Strong"}
                  </button>
                ))}
              </div>
            </div>

            {/* Loading skeletons */}
            {loading ? (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {[1,2,3,4,5].map(i=>(
                  <div key={i} style={{ background:C.white,
                    border:`1.5px solid ${C.border}`,
                    borderRadius:14, padding:"16px" }}>
                    <div style={{ display:"flex", gap:12 }}>
                      <div className="skeleton" style={{ width:42,height:42,borderRadius:10,flexShrink:0 }}/>
                      <div style={{ flex:1 }}>
                        <div className="skeleton" style={{ height:14,width:"55%",marginBottom:8 }}/>
                        <div className="skeleton" style={{ height:11,width:"35%",marginBottom:6 }}/>
                        <div className="skeleton" style={{ height:11,width:"45%" }}/>
                      </div>
                      <div className="skeleton" style={{ width:46,height:46,borderRadius:"50%",flexShrink:0 }}/>
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign:"center", padding:"60px 20px", color:C.inkL }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:18,
                  fontWeight:800, color:C.inkM, marginBottom:6 }}>
                  No jobs found
                </div>
                <div style={{ fontSize:13, marginBottom:16 }}>
                  Try adjusting your filters or search terms
                </div>
                <button onClick={()=>{ setFilterMatch("all"); setSearchQ(""); }}
                  style={{ padding:"9px 20px", background:C.green, color:"#fff",
                    border:"none", borderRadius:9, fontWeight:700,
                    fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                  Clear filters
                </button>
              </div>
            ) : (
              <div>
                {filtered.slice(0, visibleCount).map((job, i) => (
                  <JobCard key={job.id} job={job} profile={profile}
                    selected={selected?.id === job.id}
                    onSelect={setSelected}
                    onSave={handleSave}
                    saved={saved.has(job.id)}
                    index={i}/>
                ))}
                {visibleCount < filtered.length && (
                  <div style={{ textAlign:"center", padding:"20px 0 8px" }}>
                    <button
                      onClick={() => setVisibleCount(v => v + 12)}
                      style={{ padding:"12px 32px", background:"#fff",
                        border:"1.5px solid #0a6e4e", borderRadius:10,
                        color:"#0a6e4e", fontWeight:700, fontSize:14,
                        cursor:"pointer", fontFamily:"inherit",
                        transition:"all .15s" }}
                      onMouseOver={e => { e.target.style.background="#0a6e4e"; e.target.style.color="#fff"; }}
                      onMouseOut={e => { e.target.style.background="#fff"; e.target.style.color="#0a6e4e"; }}>
                      Load More ({filtered.length - visibleCount} remaining)
                    </button>
                  </div>
                )}
                {visibleCount >= filtered.length && filtered.length > 12 && (
                  <div style={{ textAlign:"center", padding:"16px 0 8px",
                    fontSize:13, color:"#6b7a8d" }}>
                    All {filtered.length} jobs loaded
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{ position:"sticky", top:80,
              height:"calc(100vh - 100px)",
              background:C.white, borderRadius:16,
              border:`1.5px solid ${C.border}`,
              overflow:"hidden",
              boxShadow:"0 8px 32px rgba(0,0,0,.08)" }}>
              <JobDetail
                job={selected}
                profile={profile}
                onApply={handleApply}
                onSave={handleSave}
                saved={saved.has(selected?.id)}
                onClose={()=>setSelected(null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
